import { useState, useEffect, useCallback } from "react";
import {
  Activity, Download, Search, Phone, Filter, Calendar,
  ChevronLeft, ChevronRight, Loader2, BarChart3, Globe, FileSpreadsheet,
  ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const apiCall = async (method, url) => {
  const res = await fetch(url, { method, credentials: "include" });
  return { ok: res.ok, data: await res.json() };
};

const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const toART = (isoStr) => {
  if (!isoStr) return null;
  const d = new Date(isoStr.endsWith("Z") || isoStr.includes("+") ? isoStr : isoStr + "Z");
  // Argentina = UTC-3
  const art = new Date(d.getTime() - 3 * 60 * 60 * 1000);
  return art;
};
const fmtDate = (d) => {
  const art = toART(d);
  if (!art) return "";
  const dd = String(art.getUTCDate()).padStart(2, "0");
  const mm = String(art.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = art.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
};
const fmtTime = (d) => {
  const art = toART(d);
  if (!art) return "";
  return `${String(art.getUTCHours()).padStart(2, "0")}:${String(art.getUTCMinutes()).padStart(2, "0")}`;
};

const MiniBar = ({ value, max, color }) => (
  <div className="h-2 bg-zinc-800 rounded-full flex-1 overflow-hidden">
    <div className={`h-full rounded-full ${color}`} style={{ width: `${max > 0 ? Math.max((value / max) * 100, 1) : 0}%` }} />
  </div>
);

const tipoBadge = (tipo) => {
  if (tipo === "CELULAR") return "bg-emerald-500/10 text-emerald-400";
  if (tipo === "FIJO") return "bg-cyan-500/10 text-cyan-400";
  return "bg-rose-500/10 text-rose-400";
};

const LogRow = ({ log }) => {
  const [open, setOpen] = useState(false);
  const sm = log.results_summary || {};
  const details = log.phones_detail || [];
  const hasDetail = details.length > 0;

  return (
    <>
      <tr
        className={`border-b border-zinc-800/40 hover:bg-zinc-800/30 ${hasDetail ? "cursor-pointer" : ""}`}
        onClick={() => hasDetail && setOpen(o => !o)}
        data-testid="log-row"
      >
        <td className="py-2 pl-2 text-zinc-500">
          {hasDetail && (open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
        </td>
        <td className="py-2 text-zinc-400 text-xs font-mono">{fmtDate(log.timestamp)} {fmtTime(log.timestamp)}</td>
        <td className="py-2 text-zinc-300 text-xs truncate max-w-[150px]">{log.company_name}</td>
        <td className="py-2"><Badge className="text-[10px] bg-cyan-500/10 text-cyan-400 border-cyan-500/20">{log.endpoint}</Badge></td>
        <td className="py-2 text-right text-white font-mono">{log.phone_count}</td>
        <td className="py-2 text-right text-emerald-400 font-mono">{sm.celular || 0}</td>
        <td className="py-2 text-right text-cyan-400 font-mono">{sm.fijo || 0}</td>
        <td className="py-2 text-right text-rose-400 font-mono">{sm.invalido || 0}</td>
        <td className="py-2 text-right pr-2">{log.check_whatsapp ? <Badge className="text-[10px] bg-green-500/10 text-green-400">SI</Badge> : <span className="text-zinc-600">-</span>}</td>
      </tr>
      {open && details.map((ph, j) => (
        <tr key={j} className="bg-zinc-800/40 border-b border-zinc-800/20" data-testid="log-detail-row">
          <td className="py-1.5 pl-2"></td>
          <td className="py-1.5" colSpan={2}>
            <span className="text-white text-xs font-mono">{ph.original}</span>
            {ph.normalizado && ph.normalizado !== ph.original && (
              <span className="text-zinc-500 text-[10px] ml-2">→ {ph.normalizado}</span>
            )}
          </td>
          <td className="py-1.5">
            <Badge className={`text-[10px] ${tipoBadge(ph.tipo)}`}>{ph.tipo || "?"}</Badge>
          </td>
          <td className="py-1.5 text-right text-zinc-400 text-[11px]" colSpan={2}>
            {ph.provincia && <span>{ph.provincia}</span>}
          </td>
          <td className="py-1.5 text-right text-zinc-500 text-[11px]" colSpan={2}>
            {ph.operador && <span className="truncate max-w-[140px] inline-block">{ph.operador}</span>}
          </td>
          <td className="py-1.5 text-right pr-2">
            {ph.tiene_whatsapp === "SI" ? <Badge className="text-[10px] bg-green-500/10 text-green-400">SI</Badge>
              : ph.tiene_whatsapp === "NO" ? <Badge className="text-[10px] bg-zinc-600/20 text-zinc-400">NO</Badge>
              : ph.tiene_whatsapp ? <Badge className="text-[10px] bg-rose-500/10 text-rose-400">{ph.tiene_whatsapp}</Badge>
              : <span className="text-zinc-700">-</span>}
          </td>
        </tr>
      ))}
    </>
  );
};

export const ApiUsageTab = () => {
  const [view, setView] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState({ logs: [], total: 0 });
  const [phones, setPhones] = useState({ phones: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [page, setPage] = useState(1);
  const [availableKeys, setAvailableKeys] = useState([]);
  const [selectedKeyId, setSelectedKeyId] = useState("");
  const pageSize = 50;

  // Fetch available keys on mount
  useEffect(() => {
    (async () => {
      try {
        const { ok, data } = await apiCall("GET", `${API}/api-usage/keys`);
        if (ok && data.keys) setAvailableKeys(data.keys);
      } catch {}
    })();
  }, []);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (selectedKeyId) p.set("key_id", selectedKeyId);
    return p.toString();
  }, [dateFrom, dateTo, selectedKeyId]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const { ok, data } = await apiCall("GET", `${API}/api-usage/summary?${params}`);
      if (ok) setSummary(data);
    } catch {}
    setLoading(false);
  }, [buildParams]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const { ok, data } = await apiCall("GET", `${API}/api-usage/log?${params}&page=${page}&page_size=${pageSize}`);
      if (ok) setLogs(data);
    } catch {}
    setLoading(false);
  }, [buildParams, page]);

  const fetchPhones = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const qParam = searchQ ? `&q=${encodeURIComponent(searchQ)}` : "";
      const { ok, data } = await apiCall("GET", `${API}/api-usage/phones?${params}${qParam}&page=${page}&page_size=${pageSize}`);
      if (ok) setPhones(data);
    } catch {}
    setLoading(false);
  }, [buildParams, searchQ, page]);

  useEffect(() => {
    if (view === "summary") fetchSummary();
    else if (view === "logs") fetchLogs();
    else if (view === "phones") fetchPhones();
  }, [view, fetchSummary, fetchLogs, fetchPhones]);

  const handleExport = async (exportType, format) => {
    const params = buildParams();
    const url = `${API}/api-usage/export?${params}&export_type=${exportType}&format=${format}`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) { toast.error("Error al exportar"); return; }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `api_usage_${exportType}.${format}`;
      a.click();
      toast.success("Exportado correctamente");
    } catch { toast.error("Error al exportar"); }
  };

  const totalPages = view === "logs" ? Math.ceil(logs.total / pageSize) : Math.ceil(phones.total / pageSize);

  return (
    <div className="space-y-4" data-testid="api-usage-tab">
      {/* Filters */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-4 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-zinc-500" />
            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="bg-zinc-800 border-zinc-700 text-white h-8 text-xs w-36" data-testid="date-from" />
            <span className="text-zinc-600 text-xs">a</span>
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="bg-zinc-800 border-zinc-700 text-white h-8 text-xs w-36" data-testid="date-to" />
          </div>
          {availableKeys.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-zinc-500" />
              <select
                value={selectedKeyId}
                onChange={e => { setSelectedKeyId(e.target.value); setPage(1); }}
                className="bg-zinc-800 border border-zinc-700 text-white h-8 text-xs rounded-md px-2 min-w-[160px]"
                data-testid="key-filter"
              >
                <option value="">Todas las APIs</option>
                {availableKeys.map(k => (
                  <option key={k.key_id} value={k.key_id}>
                    {k.company_name} ({k.key_short})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex gap-1 ml-auto">
            {[
              { id: "summary", label: "Resumen", icon: BarChart3 },
              { id: "logs", label: "Solicitudes", icon: Activity },
              { id: "phones", label: "Numeros", icon: Phone },
            ].map(v => (
              <Button key={v.id} size="sm" onClick={() => { setView(v.id); setPage(1); }}
                className={`h-8 text-xs ${view === v.id ? "bg-violet-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}
                data-testid={`view-${v.id}`}>
                <v.icon className="w-3 h-3 mr-1" />{v.label}
              </Button>
            ))}
          </div>
          <div className="flex gap-1">
            <Button size="sm" onClick={() => handleExport(view === "phones" ? "phones" : "summary", "csv")}
              className="h-8 text-xs bg-emerald-700 hover:bg-emerald-600 text-white" data-testid="export-csv">
              <Download className="w-3 h-3 mr-1" />CSV
            </Button>
            <Button size="sm" onClick={() => handleExport(view === "phones" ? "phones" : "summary", "xlsx")}
              className="h-8 text-xs bg-blue-700 hover:bg-blue-600 text-white" data-testid="export-xlsx">
              <FileSpreadsheet className="w-3 h-3 mr-1" />Excel
            </Button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      )}

      {/* Summary View */}
      {!loading && view === "summary" && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Solicitudes", value: summary.totals.total_calls, color: "bg-violet-500/15 text-violet-400" },
              { label: "Numeros", value: summary.totals.total_phones, color: "bg-blue-500/15 text-blue-400" },
              { label: "Celulares", value: summary.totals.total_celular, color: "bg-emerald-500/15 text-emerald-400" },
              { label: "Fijos", value: summary.totals.total_fijo, color: "bg-cyan-500/15 text-cyan-400" },
              { label: "Invalidos", value: summary.totals.total_invalido, color: "bg-rose-500/15 text-rose-400" },
            ].map(s => (
              <div key={s.label} className="bg-zinc-900/60 border border-white/10 rounded-lg p-4">
                <p className="text-2xl font-bold text-white">{(s.value || 0).toLocaleString("es-AR")}</p>
                <p className="text-xs text-zinc-500">{s.label}</p>
              </div>
            ))}
          </div>

          {/* By Period */}
          {summary.periods?.length > 0 && (
            <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="usage-periods">
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Solicitudes por dia</p>
              <div className="flex items-end gap-1 h-28">
                {summary.periods.slice(-30).map((p, i) => {
                  const max = Math.max(...summary.periods.slice(-30).map(x => x.calls), 1);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
                      <span className="text-[8px] text-zinc-500 font-mono">{p.calls > 0 ? p.calls : ""}</span>
                      <div className="w-full rounded-t bg-violet-500 transition-all" style={{ height: `${Math.max((p.calls / max) * 100, 2)}%`, minHeight: "2px" }} />
                      <span className="text-[8px] text-zinc-600 truncate w-full text-center">{p.date?.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* By Key */}
          {summary.by_key?.length > 0 && (
            <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="usage-by-key">
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Por API Key</p>
              <div className="space-y-2">
                {summary.by_key.map((k) => (
                  <div key={k.key_id} className="flex items-center gap-3">
                    <span className="text-sm text-zinc-300 w-40 truncate">{k.company_name || k.key_id?.slice(0, 8)}</span>
                    <MiniBar value={k.calls} max={summary.by_key[0]?.calls || 1} color="bg-violet-500" />
                    <span className="text-xs text-white font-mono w-16 text-right">{k.calls} req</span>
                    <span className="text-xs text-zinc-500 font-mono w-20 text-right">{k.phones} nums</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By Endpoint */}
          {summary.by_endpoint?.length > 0 && (
            <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="usage-by-endpoint">
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Por Endpoint</p>
              <div className="space-y-2">
                {summary.by_endpoint.map((e) => (
                  <div key={e.endpoint} className="flex items-center gap-3">
                    <code className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded w-40 truncate">{e.endpoint}</code>
                    <MiniBar value={e.calls} max={summary.by_endpoint[0]?.calls || 1} color="bg-cyan-500" />
                    <span className="text-xs text-white font-mono w-16 text-right">{e.calls}</span>
                    <span className="text-xs text-zinc-500 font-mono w-20 text-right">{e.phones} nums</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Logs View */}
      {!loading && view === "logs" && (
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="usage-logs-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                  <th className="w-6 py-2 pl-2"></th>
                  <th className="text-left py-2">Fecha</th>
                  <th className="text-left py-2">Empresa</th>
                  <th className="text-left py-2">Endpoint</th>
                  <th className="text-right py-2">Nums</th>
                  <th className="text-right py-2">Cel</th>
                  <th className="text-right py-2">Fijo</th>
                  <th className="text-right py-2">Inv</th>
                  <th className="text-right py-2 pr-2">WA</th>
                </tr>
              </thead>
              <tbody>
                {logs.logs?.map((log, i) => (
                  <LogRow key={i} log={log} />
                ))}
                {(!logs.logs || logs.logs.length === 0) && (
                  <tr><td colSpan={9} className="py-8 text-center text-zinc-600 text-sm">Sin solicitudes en el periodo seleccionado</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {logs.total > pageSize && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
              <span className="text-xs text-zinc-500">{logs.total} solicitudes total</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs text-zinc-400">
                  <ChevronLeft className="w-3 h-3" />Anterior
                </Button>
                <span className="text-xs text-zinc-400">{page}/{totalPages}</span>
                <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs text-zinc-400">
                  Siguiente<ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phones View */}
      {!loading && view === "phones" && (
        <div className="bg-zinc-900/60 border border-white/10 rounded-lg p-4" data-testid="usage-phones-table">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-zinc-500" />
            <Input value={searchQ} onChange={e => { setSearchQ(e.target.value); setPage(1); }}
              placeholder="Buscar numero..." className="bg-zinc-800 border-zinc-700 text-white h-8 text-xs w-64"
              onKeyDown={e => e.key === "Enter" && fetchPhones()} data-testid="search-phone" />
            <Button size="sm" onClick={fetchPhones} className="h-8 text-xs bg-zinc-700 text-zinc-300">Buscar</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-xs border-b border-zinc-800">
                  <th className="text-left py-2 pl-2">Fecha</th>
                  <th className="text-left py-2">Empresa</th>
                  <th className="text-left py-2">Numero Original</th>
                  <th className="text-left py-2">Normalizado</th>
                  <th className="text-left py-2">Tipo</th>
                  <th className="text-left py-2">Provincia</th>
                  <th className="text-left py-2">Localidad</th>
                  <th className="text-left py-2">Operador</th>
                  <th className="text-right py-2 pr-2">WA</th>
                </tr>
              </thead>
              <tbody>
                {phones.phones?.map((ph, i) => (
                  <tr key={i} className="border-b border-zinc-800/40 hover:bg-zinc-800/30">
                    <td className="py-1.5 pl-2 text-zinc-500 text-[11px] font-mono">{fmtDate(ph.timestamp)} {fmtTime(ph.timestamp)}</td>
                    <td className="py-1.5 text-zinc-400 text-xs truncate max-w-[100px]">{ph.company_name}</td>
                    <td className="py-1.5 text-white text-xs font-mono">{ph.original}</td>
                    <td className="py-1.5 text-zinc-300 text-xs font-mono">{ph.normalizado}</td>
                    <td className="py-1.5">
                      <Badge className={`text-[10px] ${ph.tipo === "CELULAR" ? "bg-emerald-500/10 text-emerald-400" : ph.tipo === "FIJO" ? "bg-cyan-500/10 text-cyan-400" : "bg-rose-500/10 text-rose-400"}`}>
                        {ph.tipo || "?"}
                      </Badge>
                    </td>
                    <td className="py-1.5 text-zinc-400 text-xs truncate max-w-[100px]">{ph.provincia}</td>
                    <td className="py-1.5 text-zinc-400 text-xs truncate max-w-[100px]">{ph.localidad}</td>
                    <td className="py-1.5 text-zinc-400 text-xs truncate max-w-[100px]">{ph.operador}</td>
                    <td className="py-1.5 text-right pr-2">
                      {ph.tiene_whatsapp === "SI" ? <Badge className="text-[10px] bg-green-500/10 text-green-400">SI</Badge>
                        : ph.tiene_whatsapp === "NO" ? <Badge className="text-[10px] bg-zinc-600/20 text-zinc-400">NO</Badge>
                        : ph.tiene_whatsapp ? <Badge className="text-[10px] bg-rose-500/10 text-rose-400">{ph.tiene_whatsapp}</Badge>
                        : <span className="text-zinc-700">-</span>}
                    </td>
                  </tr>
                ))}
                {(!phones.phones || phones.phones.length === 0) && (
                  <tr><td colSpan={9} className="py-8 text-center text-zinc-600 text-sm">
                    {phones.total === 0 ? "Sin numeros consultados en el periodo. Los detalles solo se guardan para nuevas consultas." : "Sin resultados"}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {phones.total > pageSize && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
              <span className="text-xs text-zinc-500">{phones.total} numeros total</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs text-zinc-400">
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <span className="text-xs text-zinc-400">{page}/{totalPages}</span>
                <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs text-zinc-400">
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
